import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Badge from "@mui/material/Badge";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";

import { useAuth } from "AuthContext";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { API_URL } from "config";
import { navbarIconButton } from "examples/Navbars/DashboardNavbar/styles";
import { hasExplicitPermission } from "utils/granularPermissions";

import { useProjectLiveChat } from "./useProjectLiveChat";

const GLOBAL_CHAT_INBOX_PERMISSION = "chat.global_inbox.view";
const INBOX_PAGE_LIMIT = 100;

const formatMessageTime = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const normalizeInboxRow = (row) => {
  const projectId = Number(row?.project?.id);
  const unreadCount = Number(row?.unread_count || 0);

  if (!Number.isInteger(projectId) || projectId <= 0 || unreadCount <= 0) {
    return null;
  }

  return {
    ...row,
    project: {
      id: projectId,
      quest_code: row?.project?.quest_code || String(projectId),
      project_title: row?.project?.project_title || "",
    },
    unread_count: Math.floor(unreadCount),
  };
};

export default function ProjectLiveChatInboxMenu({ light = false }) {
  const { user, permissions = [], userPermission = [] } = useAuth();
  const {
    canUseProjectLiveChat,
    openProjectLiveChat,
    projectLiveChatUnreadRevision,
  } = useProjectLiveChat();

  const [anchorEl, setAnchorEl] = useState(null);
  const [inboxRows, setInboxRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const requestControllerRef = useRef(null);

  const grantedPermissions = useMemo(
    () => Array.from(new Set([...(permissions || []), ...(userPermission || [])])),
    [permissions, userPermission]
  );
  const canViewGlobalInbox = Boolean(
    canUseProjectLiveChat &&
      hasExplicitPermission(grantedPermissions, GLOBAL_CHAT_INBOX_PERMISSION)
  );

  const refreshInbox = useCallback(
    async ({ showLoading = false } = {}) => {
      if (!canViewGlobalInbox) return;

      const token = localStorage.getItem("authToken");
      if (!token) {
        setInboxRows([]);
        return;
      }

      requestControllerRef.current?.abort();
      const controller = new AbortController();
      requestControllerRef.current = controller;

      if (showLoading) setLoading(true);
      setLoadError("");

      try {
        const rowsByProject = new Map();
        const visitedCursors = new Set();
        let cursor = null;
        let hasMore = true;

        while (hasMore && !controller.signal.aborted) {
          const query = new URLSearchParams({
            unread_only: "1",
            limit: String(INBOX_PAGE_LIMIT),
          });
          if (cursor) query.set("cursor", String(cursor));

          const response = await fetch(
            API_URL + "/project-chat/inbox?" + query.toString(),
            {
              headers: {
                Authorization: "Bearer " + token,
                Accept: "application/json",
              },
              signal: controller.signal,
            }
          );
          const payload = await response.json().catch(() => ({}));

          if (!response.ok || payload?.success === 0) {
            throw new Error(
              payload?.message || "Unread project chats could not be loaded."
            );
          }

          const pageRows = Array.isArray(payload?.projects) ? payload.projects : [];
          pageRows.forEach((row) => {
            const normalized = normalizeInboxRow(row);
            if (normalized && !rowsByProject.has(normalized.project.id)) {
              rowsByProject.set(normalized.project.id, normalized);
            }
          });

          const nextCursor = Number(payload?.meta?.next_cursor);
          hasMore = Boolean(payload?.meta?.has_more) && Number.isInteger(nextCursor);
          if (!hasMore || nextCursor <= 0 || visitedCursors.has(nextCursor)) {
            hasMore = false;
          } else {
            visitedCursors.add(nextCursor);
            cursor = nextCursor;
          }
        }

        if (!controller.signal.aborted) {
          setInboxRows(Array.from(rowsByProject.values()));
        }
      } catch (error) {
        if (error?.name !== "AbortError" && !controller.signal.aborted) {
          setLoadError(error?.message || "Unread project chats could not be loaded.");
        }
      } finally {
        if (requestControllerRef.current === controller) {
          requestControllerRef.current = null;
          setLoading(false);
        }
      }
    },
    [canViewGlobalInbox]
  );

  useEffect(() => {
    if (!canViewGlobalInbox) {
      requestControllerRef.current?.abort();
      setAnchorEl(null);
      setInboxRows([]);
      setLoadError("");
      setLoading(false);
      return;
    }

    void refreshInbox({ showLoading: true });
  }, [
    canViewGlobalInbox,
    projectLiveChatUnreadRevision,
    refreshInbox,
    user?.id,
  ]);

  useEffect(() => {
    if (!canViewGlobalInbox) return undefined;

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        void refreshInbox();
      }
    };

    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [canViewGlobalInbox, refreshInbox]);

  useEffect(
    () => () => {
      requestControllerRef.current?.abort();
    },
    []
  );

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    void refreshInbox({ showLoading: inboxRows.length === 0 });
  };

  const handleClose = () => setAnchorEl(null);

  const handleProjectClick = (row) => {
    if (openProjectLiveChat(row.project)) {
      handleClose();
    }
  };

  if (!canViewGlobalInbox) return null;

  return (
    <>
      <Tooltip title="Unread project chats">
        <IconButton
          size="small"
          color="inherit"
          sx={navbarIconButton}
          onClick={handleOpen}
          aria-label="Open unread project chats"
          aria-controls={anchorEl ? "project-live-chat-inbox-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={anchorEl ? "true" : undefined}
        >
          <Badge
            badgeContent={inboxRows.length}
            color="error"
            max={99}
            invisible={inboxRows.length === 0}
          >
            <Icon sx={{ color: light ? "#fff" : "#1e293b" }}>forum</Icon>
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        id="project-live-chat-inbox-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 360,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: 440,
            overflowY: "auto",
          },
        }}
      >
        <SoftBox
          px={2}
          py={1}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <SoftTypography variant="button" fontWeight="bold">
            Unread Project Chats
          </SoftTypography>
          {loading && <CircularProgress size={16} />}
        </SoftBox>

        {!loading && loadError && (
          <MenuItem onClick={() => void refreshInbox({ showLoading: true })}>
            <SoftTypography variant="caption" color="error">
              {loadError} Click to retry.
            </SoftTypography>
          </MenuItem>
        )}

        {!loading && !loadError && inboxRows.length === 0 && (
          <SoftBox px={2} py={1.5}>
            <SoftTypography variant="caption" color="text">
              No unread project chats
            </SoftTypography>
          </SoftBox>
        )}

        {inboxRows.map((row) => {
          const projectCode = row.project.quest_code;
          const projectTitle = row.project.project_title;
          const latestMessage = row.latest_message;
          const messageTime = formatMessageTime(latestMessage?.created_at);

          return (
            <MenuItem
              key={row.project.id}
              onClick={() => handleProjectClick(row)}
              sx={{ alignItems: "flex-start", py: 1.25, whiteSpace: "normal" }}
            >
              <SoftBox width="100%">
                <SoftBox display="flex" justifyContent="space-between" gap={1}>
                  <SoftTypography variant="button" fontWeight="bold" color="info">
                    {projectCode}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="error" fontWeight="medium">
                    {row.unread_count} unread
                  </SoftTypography>
                </SoftBox>

                {projectTitle && (
                  <SoftTypography
                    variant="caption"
                    color="dark"
                    display="block"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {projectTitle}
                  </SoftTypography>
                )}

                <SoftTypography
                  variant="caption"
                  color="text"
                  display="block"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {latestMessage?.sender?.name
                    ? latestMessage.sender.name + ": "
                    : ""}
                  {latestMessage?.body || "New project chat message"}
                </SoftTypography>

                {messageTime && (
                  <SoftTypography variant="caption" color="secondary">
                    {messageTime}
                  </SoftTypography>
                )}
              </SoftBox>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

ProjectLiveChatInboxMenu.propTypes = {
  light: PropTypes.bool,
};
